import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PageLayout from '../components/layout/PageLayout';
import { trainingProgressApi } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

const FONT = "'Barlow', sans-serif";
const STORAGE_KEY = 'pm_training_pack';
const PROGRESS_STORAGE_KEY = 'pm_training_progress_v1';
const COLORS = {
  bg: '#0d0d0d',
  card: '#111',
  cardAlt: '#1a1a1a',
  border: 'rgba(255,255,255,0.06)',
  white: '#fff',
  muted: 'rgba(255,255,255,0.35)',
  green: '#00E633',
  blue: '#009BFF',
  lime: '#C8FF00',
  red: '#e53935',
};

type DrillAction =
  | 'started'
  | 'coach_opened'
  | 'completed'
  | 'repetition'
  | 'note_saved'
  | 'reset_drill'
  | 'reset_all';

interface LeakItem {
  name: string;
  category: string;
  cost_bb: number;
  severity: number;
}

interface DrillItem {
  name: string;
  reason: string;
  href: string;
}

interface TrainingPack {
  generatedAt: string;
  focusDrill: string;
  session: {
    id: string | number;
    filename: string;
    created_at: string;
    hands_count: number;
    winrate: number;
    ev_total: number;
    roi: number;
    score: number;
  };
  leaks: LeakItem[];
  drills: DrillItem[];
}

interface TrainingPackLocationState {
  trainingPack?: TrainingPack;
}

interface DrillProgress {
  drillName: string;
  timesStarted: number;
  timesCompleted: number;
  repetitions: number;
  streak: number;
  coachLaunches: number;
  notes: string;
  lastPracticedAt?: string;
  lastCompletedAt?: string;
}

interface PracticeHistoryEntry {
  id: string;
  drillName: string;
  action: DrillAction;
  timestamp: string;
  sessionId: string;
  detail?: string;
  value?: number;
}

interface TrainingProgressStore {
  drills: Record<string, DrillProgress>;
  history: PracticeHistoryEntry[];
  updatedAt: string;
}

type ChartWindow = 4 | 8 | 12;

interface AuthIdentity {
  displayName: string;
  email: string;
  isAuthenticated: boolean;
  storageKey: string;
}

interface WeeklyTrack {
  id: string;
  drillName: string;
  reason: string;
  targetReps: number;
  targetBlocks: number;
  targetCoach: number;
  currentReps: number;
  currentBlocks: number;
  currentCoach: number;
  completion: number;
  statusLabel: string;
  statusTone: 'good' | 'warn' | 'muted';
}

type SyncMode = 'local' | 'remote' | 'pending';

const MOCK_PACK: TrainingPack = {
  generatedAt: new Date().toISOString(),
  focusDrill: 'Spots de fold no BTN',
  session: {
    id: 'mock-1',
    filename: 'sessao_ggpoker_2026.txt',
    created_at: new Date().toISOString(),
    hands_count: 47,
    winrate: 8.2,
    ev_total: 2.3,
    roi: 8.2,
    score: 73,
  },
  leaks: [
    { name: 'Fold excessivo no BTN', category: 'Posicao', cost_bb: -1.8, severity: 0.7 },
    { name: 'C-bet frequencia baixa no flop', category: 'Agressividade', cost_bb: -1.2, severity: 0.5 },
    { name: 'Overlimp pre-flop no SB', category: 'Pre-flop', cost_bb: -0.8, severity: 0.3 },
  ],
  drills: [
    { name: 'Spots de fold no BTN', reason: 'Voce folda 78% aqui - meta: 55%', href: '/treinos' },
    { name: 'C-bet sizing no flop seco', reason: 'Apostas abaixo do ideal em 63% dos spots', href: '/treinos' },
    { name: 'Pre-flop: limp vs raise no SB', reason: 'Limpa 44% quando deveria subir', href: '/treinos' },
  ],
};

function formatDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Sem data';
  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'agora';
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatSigned(value: number, suffix = '') {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(1)}${suffix}`;
}

function buildCoachPrompt(drill: DrillItem, pack: TrainingPack) {
  const topLeaks = pack.leaks
    .slice(0, 3)
    .map((leak) => `${leak.name} (${Math.abs(leak.cost_bb).toFixed(1)} BB)`)
    .join(', ');

  return [
    `Monte um treino pratico para o drill "${drill.name}".`,
    `Contexto da sessao: ${pack.session.filename}, ${pack.session.hands_count} maos, winrate ${formatSigned(pack.session.winrate, ' BB/100')}, EV ${formatSigned(pack.session.ev_total, ' BB')}.`,
    `Leaks principais: ${topLeaks || 'sem leaks mapeados'}.`,
    'Quero um plano objetivo com repeticoes, checkpoints e erros mais comuns para evitar.',
  ].join(' ');
}

function readStoredPack() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrainingPack) : null;
  } catch {
    return null;
  }
}

function emptyProgressStore(): TrainingProgressStore {
  return {
    drills: {},
    history: [],
    updatedAt: new Date().toISOString(),
  };
}

function readAuthIdentity(): AuthIdentity {
  if (typeof window === 'undefined') {
    return {
      displayName: 'Visitante',
      email: '',
      isAuthenticated: false,
      storageKey: PROGRESS_STORAGE_KEY,
    };
  }

  try {
    const raw = window.localStorage.getItem('pm_auth');
    const parsed = raw ? (JSON.parse(raw) as { token?: string; user?: { id?: number; email?: string; name?: string } }) : null;
    const user = parsed?.user;
    const identifier = user?.id ?? user?.email ?? null;

    return {
      displayName: user?.name ?? 'Visitante',
      email: user?.email ?? '',
      isAuthenticated: Boolean(parsed?.token && identifier),
      storageKey: identifier ? `${PROGRESS_STORAGE_KEY}:${identifier}` : PROGRESS_STORAGE_KEY,
    };
  } catch {
    return {
      displayName: 'Visitante',
      email: '',
      isAuthenticated: false,
      storageKey: PROGRESS_STORAGE_KEY,
    };
  }
}

function mergeProgressStores(
  localStore: TrainingProgressStore,
  remoteStore: TrainingProgressStore | null
) {
  if (!remoteStore) return localStore;

  const localUpdated = new Date(localStore.updatedAt).getTime();
  const remoteUpdated = new Date(remoteStore.updatedAt).getTime();

  if (Number.isNaN(remoteUpdated)) return localStore;
  if (Number.isNaN(localUpdated)) return remoteStore;

  return remoteUpdated > localUpdated ? remoteStore : localStore;
}

function normalizeImportedProgressStore(input: unknown): TrainingProgressStore | null {
  if (!input || typeof input !== 'object') return null;

  const candidate = input as Partial<TrainingProgressStore> & {
    progressStore?: TrainingProgressStore;
  };
  const source = candidate.progressStore ?? candidate;
  const drills = source.drills;
  const history = source.history;

  if (!drills || typeof drills !== 'object' || !Array.isArray(history)) {
    return null;
  }

  const normalizedDrills = Object.entries(drills).reduce<Record<string, DrillProgress>>(
    (acc, [key, value]) => {
      if (!value || typeof value !== 'object') return acc;
      const item = value as Partial<DrillProgress>;
      acc[key] = {
        drillName: item.drillName ?? key,
        timesStarted: Number(item.timesStarted ?? 0),
        timesCompleted: Number(item.timesCompleted ?? 0),
        repetitions: Number(item.repetitions ?? 0),
        streak: Number(item.streak ?? 0),
        coachLaunches: Number(item.coachLaunches ?? 0),
        notes: typeof item.notes === 'string' ? item.notes : '',
        lastPracticedAt: item.lastPracticedAt,
        lastCompletedAt: item.lastCompletedAt,
      };
      return acc;
    },
    {}
  );

  const normalizedHistory = history
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry, index) => {
      const item = entry as Partial<PracticeHistoryEntry>;
      return {
        id: item.id ?? `imported-${index}`,
        drillName: item.drillName ?? 'Drill importado',
        action: (item.action as DrillAction) ?? 'started',
        timestamp: item.timestamp ?? new Date().toISOString(),
        sessionId: item.sessionId ?? 'imported',
        detail: item.detail,
        value: typeof item.value === 'number' ? item.value : undefined,
      };
    })
    .slice(0, 50);

  return {
    drills: normalizedDrills,
    history: normalizedHistory,
    updatedAt:
      typeof source.updatedAt === 'string' ? source.updatedAt : new Date().toISOString(),
  };
}

function readStoredProgress(storageKey: string) {
  if (typeof window === 'undefined') return emptyProgressStore();

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw) as TrainingProgressStore;

    if (storageKey !== PROGRESS_STORAGE_KEY) {
      const legacy = window.localStorage.getItem(PROGRESS_STORAGE_KEY);
      if (legacy) return JSON.parse(legacy) as TrainingProgressStore;
    }

    return emptyProgressStore();
  } catch {
    return emptyProgressStore();
  }
}

function normalizeDrillKey(drillName: string) {
  return drillName.trim().toLowerCase();
}

function getOrCreateProgress(store: TrainingProgressStore, drillName: string): DrillProgress {
  const key = normalizeDrillKey(drillName);
  return (
    store.drills[key] ?? {
      drillName,
      timesStarted: 0,
      timesCompleted: 0,
      repetitions: 0,
      streak: 0,
      coachLaunches: 0,
      notes: '',
    }
  );
}

function actionLabel(action: DrillAction) {
  if (action === 'started') return 'Bloco iniciado';
  if (action === 'coach_opened') return 'Coach aberto';
  if (action === 'completed') return 'Bloco concluido';
  if (action === 'repetition') return 'Repeticoes adicionadas';
  if (action === 'reset_drill') return 'Drill resetado';
  if (action === 'reset_all') return 'Progresso global resetado';
  return 'Notas salvas';
}

function startOfWeek(date: Date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + diff);
  return next;
}

function formatWeekLabel(date: Date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}`;
}

export default function Treinos() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const locationPack = (location.state as TrainingPackLocationState | null)?.trainingPack;
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const authIdentity = useMemo(() => readAuthIdentity(), []);
  const progressStorageKey = authIdentity.storageKey;
  const [progressStore, setProgressStore] = useState<TrainingProgressStore>(() =>
    readStoredProgress(progressStorageKey)
  );
  const [chartWindow, setChartWindow] = useState<ChartWindow>(8);
  const [importFeedback, setImportFeedback] = useState<string | null>(null);
  const [syncMode, setSyncMode] = useState<SyncMode>(
    authIdentity.isAuthenticated ? 'pending' : 'local'
  );
  const remoteHydratedRef = useRef(false);

  useEffect(() => {
    if (!locationPack || typeof window === 'undefined') return;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(locationPack));
  }, [locationPack]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(progressStorageKey, JSON.stringify(progressStore));
  }, [progressStorageKey, progressStore]);

  useEffect(() => {
    queryClient.setQueryData(queryKeys.trainingProgress, progressStore);
  }, [progressStore, queryClient]);

  useEffect(() => {
    setProgressStore(readStoredProgress(progressStorageKey));
  }, [progressStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function syncFromOtherTabs(event: StorageEvent) {
      if (event.key !== progressStorageKey) return;
      setProgressStore(readStoredProgress(progressStorageKey));
      setImportFeedback('Progresso sincronizado a partir de outra aba desta conta.');
    }

    window.addEventListener('storage', syncFromOtherTabs);
    return () => window.removeEventListener('storage', syncFromOtherTabs);
  }, [progressStorageKey]);

  useEffect(() => {
    if (!authIdentity.isAuthenticated) {
      setSyncMode('local');
      remoteHydratedRef.current = true;
      return;
    }

    let active = true;

    async function hydrateRemoteProgress() {
      try {
        const response = await trainingProgressApi.get();
        if (!active) return;

        const merged = mergeProgressStores(
          readStoredProgress(progressStorageKey),
          response?.progress as TrainingProgressStore | null
        );
        setProgressStore(merged);
        queryClient.setQueryData(queryKeys.trainingProgress, response?.progress ?? merged);
        setSyncMode(response?.progress ? 'remote' : 'local');
      } catch {
        if (!active) return;
        setSyncMode('local');
      } finally {
        remoteHydratedRef.current = true;
      }
    }

    void hydrateRemoteProgress();

    return () => {
      active = false;
    };
  }, [authIdentity.isAuthenticated, progressStorageKey, queryClient]);

  useEffect(() => {
    if (!authIdentity.isAuthenticated || !remoteHydratedRef.current) return;

    let active = true;

    async function persistRemoteProgress() {
      try {
        const response = await trainingProgressApi.save(progressStore);
        if (!active) return;
        queryClient.setQueryData(queryKeys.trainingProgress, progressStore);
        queryClient.invalidateQueries({ queryKey: queryKeys.trainingProgress });
        setSyncMode(response?.ok ? 'remote' : 'local');
      } catch {
        if (!active) return;
        setSyncMode('local');
      }
    }

    void persistRemoteProgress();

    return () => {
      active = false;
    };
  }, [authIdentity.isAuthenticated, progressStore, queryClient]);

  const pack = useMemo(() => locationPack ?? readStoredPack() ?? MOCK_PACK, [locationPack]);
  const [activeDrillName, setActiveDrillName] = useState(pack.focusDrill);
  const [draftNotes, setDraftNotes] = useState('');

  useEffect(() => {
    setActiveDrillName(pack.focusDrill);
  }, [pack.focusDrill]);

  const focusDrill =
    pack.drills.find((drill) => drill.name === activeDrillName) ??
    pack.drills.find((drill) => drill.name === pack.focusDrill) ??
    pack.drills[0] ??
    null;
  const focusLeak = pack.leaks[0] ?? null;
  const usingFallback = pack === MOCK_PACK;
  const activeProgress = focusDrill
    ? getOrCreateProgress(progressStore, focusDrill.name)
    : null;

  useEffect(() => {
    setDraftNotes(activeProgress?.notes ?? '');
  }, [activeProgress?.notes, activeDrillName]);

  const totals = useMemo(() => {
    const progressList = Object.values(progressStore.drills);
    return {
      completed: progressList.reduce((sum, item) => sum + item.timesCompleted, 0),
      repetitions: progressList.reduce((sum, item) => sum + item.repetitions, 0),
      coachLaunches: progressList.reduce((sum, item) => sum + item.coachLaunches, 0),
      streak: Math.max(0, ...progressList.map((item) => item.streak), 0),
    };
  }, [progressStore.drills]);

  const recentHistory = useMemo(
    () => progressStore.history.slice(0, 8),
    [progressStore.history]
  );

  const currentWeekStart = useMemo(() => startOfWeek(new Date()), []);

  const weeklyTracks = useMemo(() => {
    return pack.drills.slice(0, 3).map((drill, index) => {
      const drillKey = normalizeDrillKey(drill.name);
      const relatedLeak = pack.leaks[index] ?? pack.leaks[0];
      const drillEntries = progressStore.history.filter(
        (entry) =>
          normalizeDrillKey(entry.drillName) === drillKey &&
          new Date(entry.timestamp) >= currentWeekStart
      );

      const currentReps = drillEntries.reduce(
        (sum, entry) => sum + (entry.action === 'repetition' ? entry.value ?? 0 : 0),
        0
      );
      const currentBlocks = drillEntries.filter((entry) => entry.action === 'completed').length;
      const currentCoach = drillEntries.filter((entry) => entry.action === 'coach_opened').length;
      const targetReps = Math.max(
        10,
        Math.round((((relatedLeak?.severity ?? 0.45) * 18) + 10 + index * 4) / 5) * 5
      );
      const targetBlocks = index === 0 ? 3 : 2;
      const targetCoach = 1;
      const completion = Math.round(
        Math.min(
          1,
          (currentReps / targetReps) * 0.5 +
            (currentBlocks / targetBlocks) * 0.35 +
            (currentCoach / targetCoach) * 0.15
        ) * 100
      );

      let statusLabel = 'Precisa ganhar volume';
      let statusTone: WeeklyTrack['statusTone'] = 'warn';

      if (completion >= 100) {
        statusLabel = 'Meta semanal batida';
        statusTone = 'good';
      } else if (completion >= 65) {
        statusLabel = 'No ritmo certo';
        statusTone = 'good';
      } else if (completion >= 35) {
        statusLabel = 'Em construcao';
        statusTone = 'muted';
      }

      return {
        id: `${drillKey}-${index}`,
        drillName: drill.name,
        reason: drill.reason,
        targetReps,
        targetBlocks,
        targetCoach,
        currentReps,
        currentBlocks,
        currentCoach,
        completion,
        statusLabel,
        statusTone,
      };
    });
  }, [currentWeekStart, pack.drills, pack.leaks, progressStore.history]);

  const weeklyGoalSummary = useMemo(() => {
    const totalTracks = weeklyTracks.length || 1;
    const completion = Math.round(
      weeklyTracks.reduce((sum, track) => sum + track.completion, 0) / totalTracks
    );

    return {
      completion,
      completedTracks: weeklyTracks.filter((track) => track.completion >= 100).length,
      reps: weeklyTracks.reduce((sum, track) => sum + track.currentReps, 0),
      targetReps: weeklyTracks.reduce((sum, track) => sum + track.targetReps, 0),
    };
  }, [weeklyTracks]);

  const weeklyData = useMemo(() => {
    const weekMap = new Map<string, { week: string; reps: number; blocks: number }>();

    progressStore.history.forEach((entry) => {
      const weekDate = startOfWeek(new Date(entry.timestamp));
      const key = weekDate.toISOString();
      const current = weekMap.get(key) ?? {
        week: formatWeekLabel(weekDate),
        reps: 0,
        blocks: 0,
      };

      if (entry.action === 'repetition') {
        current.reps += entry.value ?? 0;
      }

      if (entry.action === 'completed') {
        current.blocks += 1;
      }

      weekMap.set(key, current);
    });

    const sorted = Array.from(weekMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-chartWindow)
      .map(([, value]) => value);

    return sorted.length
      ? sorted
      : [{ week: formatWeekLabel(startOfWeek(new Date())), reps: 0, blocks: 0 }];
  }, [chartWindow, progressStore.history]);

  function commitProgress(
    drillName: string,
    action: DrillAction,
    patch?: Partial<DrillProgress>,
    detail?: string,
    value?: number
  ) {
    setProgressStore((prev) => {
      const next = { ...prev, drills: { ...prev.drills } };
      const key = normalizeDrillKey(drillName);
      const current = getOrCreateProgress(prev, drillName);
      const updated: DrillProgress = {
        ...current,
        ...patch,
      };

      next.drills[key] = updated;
      next.history = [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          drillName,
          action,
          timestamp: new Date().toISOString(),
          sessionId: String(pack.session.id),
          detail,
          value,
        },
        ...prev.history,
      ].slice(0, 20);
      next.updatedAt = new Date().toISOString();
      return next;
    });
  }

  function handleStartDrill(drill: DrillItem) {
    const current = getOrCreateProgress(progressStore, drill.name);
    commitProgress(
      drill.name,
      'started',
      {
        drillName: drill.name,
        timesStarted: current.timesStarted + 1,
        streak: current.streak + 1,
        lastPracticedAt: new Date().toISOString(),
      },
      'Bloco de treino iniciado manualmente'
    );
  }

  function handleAddRepetitions(drill: DrillItem, amount: number) {
    const current = getOrCreateProgress(progressStore, drill.name);
    commitProgress(
      drill.name,
      'repetition',
      {
        drillName: drill.name,
        repetitions: current.repetitions + amount,
        lastPracticedAt: new Date().toISOString(),
      },
      `+${amount} repeticoes`,
      amount
    );
  }

  function handleCompleteDrill(drill: DrillItem) {
    const current = getOrCreateProgress(progressStore, drill.name);
    commitProgress(
      drill.name,
      'completed',
      {
        drillName: drill.name,
        timesCompleted: current.timesCompleted + 1,
        streak: current.streak + 1,
        lastCompletedAt: new Date().toISOString(),
        lastPracticedAt: new Date().toISOString(),
      },
      'Bloco marcado como concluido'
    );
  }

  function handleSaveNotes() {
    if (!focusDrill) return;
    const current = getOrCreateProgress(progressStore, focusDrill.name);
    commitProgress(
      focusDrill.name,
      'note_saved',
      {
        ...current,
        drillName: focusDrill.name,
        notes: draftNotes.trim(),
        lastPracticedAt: new Date().toISOString(),
      },
      draftNotes.trim() ? 'Notas atualizadas' : 'Notas limpas'
    );
  }

  function handleCoachLaunch(drill: DrillItem) {
    const current = getOrCreateProgress(progressStore, drill.name);
    commitProgress(
      drill.name,
      'coach_opened',
      {
        drillName: drill.name,
        coachLaunches: current.coachLaunches + 1,
        lastPracticedAt: new Date().toISOString(),
      },
      'Coach aberto a partir do hub de treinos'
    );
  }

  function handleResetDrill(drill: DrillItem) {
    const confirmed = window.confirm(
      `Deseja resetar o progresso do drill "${drill.name}"?`
    );
    if (!confirmed) return;

    setProgressStore((prev) => {
      const key = normalizeDrillKey(drill.name);
      const nextDrills = { ...prev.drills };
      delete nextDrills[key];

      const nextHistory = [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          drillName: drill.name,
          action: 'reset_drill' as const,
          timestamp: new Date().toISOString(),
          sessionId: String(pack.session.id),
          detail: 'Reset manual do drill',
        },
        ...prev.history.filter((entry) => normalizeDrillKey(entry.drillName) !== key),
      ].slice(0, 20);

      return {
        drills: nextDrills,
        history: nextHistory,
        updatedAt: new Date().toISOString(),
      };
    });
  }

  function handleResetAll() {
    const confirmed = window.confirm(
      'Deseja resetar todo o progresso persistido de treinos?'
    );
    if (!confirmed) return;

    setProgressStore({
      drills: {},
      history: [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          drillName: 'Geral',
          action: 'reset_all',
          timestamp: new Date().toISOString(),
          sessionId: String(pack.session.id),
          detail: 'Reset global do progresso',
        },
      ],
      updatedAt: new Date().toISOString(),
    });
  }

  function handleExportProgress() {
    if (typeof window === 'undefined') return;

    const payload = {
      exportedAt: new Date().toISOString(),
      version: 1,
      user: {
        name: authIdentity.displayName,
        email: authIdentity.email,
      },
      progressStore,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pokermind-treinos-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    setImportFeedback('Progresso exportado em JSON.');
  }

  function handleImportProgress(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result ?? '{}'));
        const normalized = normalizeImportedProgressStore(parsed);
        if (!normalized) {
          setImportFeedback('Arquivo invalido para importacao de progresso.');
          return;
        }

        setProgressStore(normalized);
        queryClient.setQueryData(queryKeys.trainingProgress, normalized);
        queryClient.invalidateQueries({ queryKey: queryKeys.trainingProgress });
        setImportFeedback('Progresso importado com sucesso.');
      } catch {
        setImportFeedback('Nao foi possivel ler o arquivo selecionado.');
      } finally {
        event.target.value = '';
      }
    };
    reader.onerror = () => {
      setImportFeedback('Falha ao abrir o arquivo de importacao.');
      event.target.value = '';
    };
    reader.readAsText(file);
  }

  return (
    <PageLayout activeRoute="/treinos">
      <style>{`
        .pm-train-root {
          min-height: calc(100vh - 64px);
          color: ${COLORS.white};
          font-family: ${FONT};
          background: ${COLORS.bg};
        }
        .pm-train-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.65fr) minmax(320px, 0.95fr);
          gap: 20px;
          align-items: start;
        }
        .pm-train-card {
          background: ${COLORS.card};
          border: 1px solid ${COLORS.border};
          border-radius: 14px;
        }
        .pm-train-stat-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
          margin-top: 20px;
        }
        .pm-train-stat {
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 16px;
        }
        .pm-train-action,
        .pm-train-button {
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          font-weight: 800;
          font-size: 12px;
          font-family: ${FONT};
          padding: 10px 16px;
        }
        .pm-train-button {
          border: none;
          cursor: pointer;
        }
        .pm-train-primary {
          background: linear-gradient(135deg, ${COLORS.green}, ${COLORS.blue});
          color: #000;
        }
        .pm-train-secondary {
          border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.8);
          background: transparent;
        }
        .pm-train-soft {
          background: rgba(255,255,255,0.05);
          color: rgba(255,255,255,0.82);
        }
        .pm-train-drill-row,
        .pm-train-leak-row,
        .pm-train-check-row,
        .pm-train-history-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          padding: 14px 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .pm-train-drill-row:last-child,
        .pm-train-leak-row:last-child,
        .pm-train-check-row:last-child,
        .pm-train-history-row:last-child {
          border-bottom: none;
        }
        .pm-train-pill {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 4px 10px;
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.45);
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .pm-train-note {
          width: 100%;
          min-height: 132px;
          resize: vertical;
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 14px;
          color: ${COLORS.white};
          font-family: ${FONT};
          font-size: 13px;
          outline: none;
        }
        .pm-train-track-card {
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 14px;
        }
        .pm-train-progress-rail {
          height: 6px;
          border-radius: 999px;
          background: rgba(255,255,255,0.08);
          overflow: hidden;
        }
        .pm-train-progress-fill {
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, ${COLORS.green}, ${COLORS.blue});
        }
        @media (max-width: 1280px) {
          .pm-train-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 920px) {
          .pm-train-stat-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 640px) {
          .pm-train-stat-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="pm-train-root">
        <input
          ref={importInputRef}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={handleImportProgress}
        />
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background:
              'linear-gradient(90deg, transparent, #00E633 30%, #009BFF 70%, transparent)',
            zIndex: 9999,
          }}
        />

        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.35)',
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                Drills Recomendados
              </div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 34,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: -0.8,
                }}
              >
                Hub de Treino da Sessao
              </h1>
              <p
                style={{
                  margin: '10px 0 0',
                  maxWidth: 720,
                  color: 'rgba(255,255,255,0.42)',
                  fontSize: 14,
                  lineHeight: 1.7,
                }}
              >
                Traduzimos os leaks da sua analise em exercicios praticos. Agora o painel tambem guarda seu progresso por drill, historico recente e notas operacionais para voce continuar de onde parou.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link to="/sessions" className="pm-train-action pm-train-secondary">
                Voltar para Analise
              </Link>
              <button
                className="pm-train-button pm-train-secondary"
                onClick={handleExportProgress}
              >
                Exportar progresso
              </button>
              <button
                className="pm-train-button pm-train-secondary"
                onClick={() => importInputRef.current?.click()}
              >
                Importar JSON
              </button>
              <button
                className="pm-train-button pm-train-secondary"
                onClick={handleResetAll}
              >
                Resetar progresso
              </button>
              {focusDrill ? (
                <Link
                  to="/coach"
                  state={{
                    preloadedPrompt: buildCoachPrompt(focusDrill, pack),
                    autoSend: true,
                  }}
                  className="pm-train-action pm-train-primary"
                  onClick={() => handleCoachLaunch(focusDrill)}
                >
                  Abrir Coach com Drill →
                </Link>
              ) : null}
            </div>
          </div>
        </div>

        {importFeedback ? (
          <div
            style={{
              marginBottom: 18,
              background: 'rgba(0,230,51,0.08)',
              border: '1px solid rgba(0,230,51,0.16)',
              borderRadius: 12,
              padding: '12px 14px',
              color: 'rgba(255,255,255,0.78)',
              fontSize: 13,
            }}
          >
            {importFeedback}
          </div>
        ) : null}

        <div
          className="pm-train-card"
          style={{
            padding: '12px 14px',
            marginBottom: 18,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'rgba(255,255,255,0.82)' }}>
              {authIdentity.isAuthenticated
                ? `Conta conectada: ${authIdentity.displayName}`
                : 'Modo local de progresso'}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              {authIdentity.isAuthenticated
                ? `${authIdentity.email || 'Conta autenticada'} · progresso vinculado ao usuario atual e sincronizado entre abas deste navegador.`
                : 'Sem conta validada para vinculo remoto. O progresso segue salvo localmente neste navegador.'}
            </div>
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              borderRadius: 999,
              padding: '6px 12px',
              background:
                syncMode === 'remote'
                  ? 'rgba(0,230,51,0.08)'
                  : syncMode === 'pending'
                  ? 'rgba(0,155,255,0.12)'
                  : 'rgba(255,255,255,0.08)',
              border:
                syncMode === 'remote'
                  ? '1px solid rgba(0,230,51,0.14)'
                  : syncMode === 'pending'
                  ? '1px solid rgba(0,155,255,0.18)'
                  : '1px solid rgba(255,255,255,0.1)',
              color:
                syncMode === 'remote'
                  ? COLORS.green
                  : syncMode === 'pending'
                  ? COLORS.blue
                  : 'rgba(255,255,255,0.72)',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {syncMode === 'remote'
              ? 'Sync remoto pronto'
              : syncMode === 'pending'
              ? 'Testando sync remoto'
              : 'Fallback local'}
          </div>
        </div>

        {usingFallback ? (
          <div
            style={{
              marginBottom: 18,
              background: 'rgba(0,155,255,0.08)',
              border: '1px solid rgba(0,155,255,0.18)',
              borderRadius: 12,
              padding: '12px 14px',
              color: 'rgba(255,255,255,0.72)',
              fontSize: 13,
            }}
          >
            Nenhum pacote de treino veio da pagina de sessoes nesta navegacao. Mantive um fallback visual para o fluxo continuar utilizavel.
          </div>
        ) : null}

        <div
          className="pm-train-card"
          style={{ padding: 22, marginBottom: 20, overflow: 'hidden' }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.45fr) minmax(280px,0.9fr)',
              gap: 20,
            }}
          >
            <div>
              <div className="pm-train-pill">Sessao selecionada</div>
              <div
                style={{
                  marginTop: 14,
                  fontSize: 28,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  textTransform: 'uppercase',
                  maxWidth: 640,
                }}
              >
                {focusDrill?.name ?? pack.focusDrill}
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.46)',
                  lineHeight: 1.7,
                  maxWidth: 720,
                }}
              >
                {focusDrill?.reason ??
                  'Treino prioritario gerado a partir da sua ultima sessao analisada.'}
              </div>

              <div className="pm-train-stat-grid">
                {[
                  {
                    label: 'WINRATE',
                    value: formatSigned(pack.session.winrate, ' BB/100'),
                    positive: pack.session.winrate >= 0,
                  },
                  {
                    label: 'EV TOTAL',
                    value: formatSigned(pack.session.ev_total, ' BB'),
                    positive: pack.session.ev_total >= 0,
                  },
                  {
                    label: 'BLOCOS',
                    value: String(totals.completed),
                    positive: true,
                  },
                  {
                    label: 'REPS',
                    value: String(totals.repetitions),
                    positive: true,
                  },
                ].map((item) => (
                  <div key={item.label} className="pm-train-stat">
                    <div
                      style={{
                        fontSize: 10,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.3)',
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 28,
                        fontWeight: 900,
                        color: item.positive ? COLORS.green : COLORS.red,
                      }}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                background:
                  'radial-gradient(circle at top left, rgba(0,230,51,0.12), transparent 48%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16,
                padding: 18,
              }}
            >
              <div
                className="pm-train-pill"
                style={{ background: 'rgba(0,230,51,0.1)', color: COLORS.green }}
              >
                Prioridade da semana
              </div>
              <div style={{ marginTop: 12, fontSize: 18, fontWeight: 800 }}>
                {focusLeak?.name ?? 'Consolidar spots de decisao'}
              </div>
              <div
                style={{
                  marginTop: 8,
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: 13,
                  lineHeight: 1.7,
                }}
              >
                Sessao {pack.session.filename} · {formatDate(pack.session.created_at)} · Score{' '}
                {pack.session.score}
              </div>
              <div
                style={{
                  marginTop: 18,
                  color: 'rgba(255,255,255,0.72)',
                  fontSize: 13,
                  lineHeight: 1.8,
                }}
              >
                Meta recomendada: reduzir o impacto dos leaks mais caros em blocos curtos de treino orientado pelo Coach, com foco em volume pequeno e feedback rapido.
              </div>
              <div
                style={{
                  marginTop: 18,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(0,155,255,0.08)',
                  border: '1px solid rgba(0,155,255,0.14)',
                }}
              >
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.4, fontWeight: 700 }}>
                  Meta semanal agregada
                </div>
                <div style={{ marginTop: 8, fontSize: 24, fontWeight: 900, color: COLORS.white }}>
                  {weeklyGoalSummary.completion}%
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,0.42)' }}>
                  {weeklyGoalSummary.completedTracks}/{weeklyTracks.length} trilhas concluidas · {weeklyGoalSummary.reps}/{weeklyGoalSummary.targetReps} reps planejadas
                </div>
              </div>
              <div style={{ marginTop: 18, display: 'grid', gap: 10 }}>
                {[
                  '1. Iniciar o bloco do drill selecionado.',
                  '2. Somar repeticoes conforme voce pratica.',
                  '3. Marcar como concluido quando fechar o objetivo.',
                ].map((item) => (
                  <div
                    key={item}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="pm-train-grid">
          <div style={{ display: 'grid', gap: 20 }}>
            <div className="pm-train-card" style={{ padding: 20 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
              >
                <span style={{ color: COLORS.blue }}>◎</span>
                <span style={{ fontSize: 15, fontWeight: 800 }}>Trilhas da Semana</span>
              </div>
              <div
                style={{ color: 'rgba(255,255,255,0.34)', fontSize: 12, marginBottom: 14 }}
              >
                Objetivos acumulados por leak para esta semana corrente
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                {[
                  { label: 'CONCLUSAO', value: `${weeklyGoalSummary.completion}%` },
                  {
                    label: 'TRILHAS BATIDAS',
                    value: String(weeklyGoalSummary.completedTracks),
                  },
                  {
                    label: 'REPS DA SEMANA',
                    value: `${weeklyGoalSummary.reps}/${weeklyGoalSummary.targetReps}`,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        letterSpacing: 1.6,
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.3)',
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900 }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                {weeklyTracks.map((track) => (
                  <div key={track.id} className="pm-train-track-card">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button
                          onClick={() => setActiveDrillName(track.drillName)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            color: COLORS.white,
                            fontSize: 14,
                            fontWeight: 800,
                            fontFamily: FONT,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {track.drillName}
                        </button>
                        <div
                          style={{
                            marginTop: 6,
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: 12,
                            lineHeight: 1.7,
                          }}
                        >
                          {track.reason}
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          borderRadius: 999,
                          padding: '5px 10px',
                          background:
                            track.statusTone === 'good'
                              ? 'rgba(0,230,51,0.1)'
                              : track.statusTone === 'warn'
                              ? 'rgba(229,57,53,0.1)'
                              : 'rgba(255,255,255,0.06)',
                          color:
                            track.statusTone === 'good'
                              ? COLORS.green
                              : track.statusTone === 'warn'
                              ? COLORS.red
                              : 'rgba(255,255,255,0.6)',
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        {track.statusLabel}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginTop: 10,
                      }}
                    >
                      <span className="pm-train-pill">
                        Reps {track.currentReps}/{track.targetReps}
                      </span>
                      <span className="pm-train-pill">
                        Blocos {track.currentBlocks}/{track.targetBlocks}
                      </span>
                      <span className="pm-train-pill">
                        Coach {track.currentCoach}/{track.targetCoach}
                      </span>
                    </div>

                    <div style={{ marginTop: 12 }} className="pm-train-progress-rail">
                      <div
                        className="pm-train-progress-fill"
                        style={{ width: `${Math.max(8, track.completion)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pm-train-card" style={{ padding: 20 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
              >
                <span style={{ color: COLORS.lime }}>⚠</span>
                <span style={{ fontSize: 15, fontWeight: 800 }}>
                  Leaks que Devem Virar Treino
                </span>
              </div>
              <div
                style={{ color: 'rgba(255,255,255,0.34)', fontSize: 12, marginBottom: 8 }}
              >
                Priorizados pelo custo em BB e pela severidade da sessao
              </div>

              {pack.leaks.map((leak) => (
                <div key={leak.name} className="pm-train-leak-row">
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.82)',
                      }}
                    >
                      {leak.name}
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span className="pm-train-pill">{leak.category}</span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                        Severidade {Math.round(leak.severity * 100)}%
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: 10,
                        height: 4,
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.06)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.max(12, Math.round(leak.severity * 100))}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #e53935, #ff6b6b)',
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      color: COLORS.red,
                      fontSize: 15,
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatSigned(leak.cost_bb, ' BB')}
                  </div>
                </div>
              ))}
            </div>

            <div className="pm-train-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>
                Bloco Ativo e Notas
              </div>
              <div
                style={{ color: 'rgba(255,255,255,0.34)', fontSize: 12, marginBottom: 14 }}
              >
                Progresso persistido por drill no navegador
              </div>

              {focusDrill && activeProgress ? (
                <>
                  <div
                    style={{
                      background: COLORS.cardAlt,
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 800 }}>{focusDrill.name}</div>
                    <div
                      style={{
                        marginTop: 8,
                        color: 'rgba(255,255,255,0.42)',
                        fontSize: 13,
                        lineHeight: 1.7,
                      }}
                    >
                      {focusDrill.reason}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        gap: 10,
                        marginTop: 16,
                      }}
                    >
                      {[
                        { label: 'INICIOS', value: activeProgress.timesStarted },
                        { label: 'CONCLUIDOS', value: activeProgress.timesCompleted },
                        { label: 'REPS', value: activeProgress.repetitions },
                        { label: 'STREAK', value: activeProgress.streak },
                      ].map((item) => (
                        <div
                          key={item.label}
                          style={{
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: 10,
                            padding: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              letterSpacing: 1.5,
                              textTransform: 'uppercase',
                              color: 'rgba(255,255,255,0.3)',
                              fontWeight: 700,
                            }}
                          >
                            {item.label}
                          </div>
                          <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900 }}>
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
                      <button
                        className="pm-train-button pm-train-primary"
                        onClick={() => handleStartDrill(focusDrill)}
                      >
                        Iniciar bloco
                      </button>
                      <button
                        className="pm-train-button pm-train-soft"
                        onClick={() => handleAddRepetitions(focusDrill, 5)}
                      >
                        +5 reps
                      </button>
                      <button
                        className="pm-train-button pm-train-soft"
                        onClick={() => handleAddRepetitions(focusDrill, 10)}
                      >
                        +10 reps
                      </button>
                      <button
                        className="pm-train-button pm-train-secondary"
                        onClick={() => handleCompleteDrill(focusDrill)}
                      >
                        Concluir bloco
                      </button>
                      <button
                        className="pm-train-button pm-train-secondary"
                        onClick={() => handleResetDrill(focusDrill)}
                      >
                        Resetar drill
                      </button>
                    </div>
                    <div
                      style={{
                        marginTop: 14,
                        color: 'rgba(255,255,255,0.35)',
                        fontSize: 12,
                        display: 'flex',
                        gap: 16,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>
                        Ultima pratica:{' '}
                        {activeProgress.lastPracticedAt
                          ? formatDateTime(activeProgress.lastPracticedAt)
                          : 'ainda nao registrada'}
                      </span>
                      <span>
                        Coach aberto: {activeProgress.coachLaunches}x
                      </span>
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                      Notas do drill
                    </div>
                    <textarea
                      className="pm-train-note"
                      value={draftNotes}
                      onChange={(event) => setDraftNotes(event.target.value)}
                      placeholder="Registre ajustes, erros recorrentes e checkpoints que voce quer revisar na proxima sessao."
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                      <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
                        Essas notas ficam salvas por drill no contexto da conta atual.
                      </div>
                      <button
                        className="pm-train-button pm-train-primary"
                        onClick={handleSaveNotes}
                      >
                        Salvar notas
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div style={{ display: 'grid', gap: 20 }}>
            <div className="pm-train-card" style={{ padding: 20 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
              >
                <span style={{ color: COLORS.green }}>↗</span>
                <span style={{ fontSize: 15, fontWeight: 800 }}>Evolucao Semanal</span>
              </div>
              <div
                style={{ color: 'rgba(255,255,255,0.34)', fontSize: 12, marginBottom: 12 }}
              >
                Repeticoes e blocos concluidos nas ultimas {chartWindow} semanas
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {([4, 8, 12] as ChartWindow[]).map((windowSize) => (
                  <button
                    key={windowSize}
                    className="pm-train-button"
                    onClick={() => setChartWindow(windowSize)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border:
                        windowSize === chartWindow
                          ? `1px solid ${COLORS.green}`
                          : '1px solid rgba(255,255,255,0.08)',
                      background:
                        windowSize === chartWindow
                          ? 'rgba(0,230,51,0.08)'
                          : 'rgba(255,255,255,0.03)',
                      color:
                        windowSize === chartWindow
                          ? COLORS.green
                          : 'rgba(255,255,255,0.62)',
                      fontSize: 11,
                    }}
                  >
                    {windowSize} semanas
                  </button>
                ))}
              </div>

              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyData} barGap={10}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis
                      dataKey="week"
                      tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <YAxis
                      tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8,
                        color: '#fff',
                        fontFamily: FONT,
                      }}
                    />
                    <Bar dataKey="reps" fill={COLORS.green} radius={[6, 6, 0, 0]} name="Reps" />
                    <Bar dataKey="blocks" fill={COLORS.blue} radius={[6, 6, 0, 0]} name="Blocos" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="pm-train-card" style={{ padding: 20 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
              >
                <span style={{ color: COLORS.blue }}>◎</span>
                <span style={{ fontSize: 15, fontWeight: 800 }}>Drills Disponiveis</span>
              </div>
              <div
                style={{ color: 'rgba(255,255,255,0.34)', fontSize: 12, marginBottom: 8 }}
              >
                Cada CTA abre o Coach com um briefing pronto para treino
              </div>

              {pack.drills.map((drill) => {
                const drillProgress = getOrCreateProgress(progressStore, drill.name);
                const isActive = focusDrill?.name === drill.name;

                return (
                  <div key={drill.name} className="pm-train-drill-row">
                    <div style={{ flex: 1 }}>
                      <button
                        onClick={() => setActiveDrillName(drill.name)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          color: isActive ? COLORS.green : 'rgba(255,255,255,0.82)',
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: FONT,
                          textAlign: 'left',
                        }}
                      >
                        {drill.name}
                      </button>
                      <div
                        style={{
                          marginTop: 6,
                          color: 'rgba(255,255,255,0.4)',
                          fontSize: 12,
                          lineHeight: 1.7,
                        }}
                      >
                        {drill.reason}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          display: 'flex',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span className="pm-train-pill">{drillProgress.repetitions} reps</span>
                        <span className="pm-train-pill">{drillProgress.timesCompleted} blocos</span>
                        <span className="pm-train-pill">streak {drillProgress.streak}</span>
                      </div>
                    </div>
                    <Link
                      to="/coach"
                      state={{
                        preloadedPrompt: buildCoachPrompt(drill, pack),
                        autoSend: true,
                      }}
                      className="pm-train-action pm-train-primary"
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => handleCoachLaunch(drill)}
                    >
                      Treinar no Coach →
                    </Link>
                  </div>
                );
              })}
            </div>

            <div className="pm-train-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>
                Historico Recente
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, lineHeight: 1.8 }}>
                Sessao: {pack.session.filename} · Score {pack.session.score}/100 · Coach aberto{' '}
                {totals.coachLaunches}x
              </div>

              <div style={{ marginTop: 12 }}>
                {recentHistory.length === 0 ? (
                  <div
                    style={{
                      color: 'rgba(255,255,255,0.38)',
                      fontSize: 13,
                      padding: '8px 0',
                    }}
                  >
                    Nenhuma acao de treino registrada ainda.
                  </div>
                ) : (
                  recentHistory.map((entry) => (
                    <div key={entry.id} className="pm-train-history-row">
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{entry.drillName}</div>
                        <div
                          style={{
                            marginTop: 4,
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: 12,
                          }}
                        >
                          {actionLabel(entry.action)}
                          {entry.detail ? ` · ${entry.detail}` : ''}
                        </div>
                      </div>
                      <div
                        style={{
                          color: 'rgba(255,255,255,0.3)',
                          fontSize: 11,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatDateTime(entry.timestamp)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
